import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { uploadFile, humanBytes } from "@/lib/files";

/**
 * A reusable drag-and-drop uploader. Renders as a dashed rectangle when
 * `compact=false`; renders as a compact strip when `compact=true`. Fires
 * onUploaded(record) for every successful upload. If `multiple=true` the
 * user can drop or select many files at once.
 */
export default function FileDropZone({
  context = {},
  multiple = true,
  compact = false,
  accept,
  maxMB = 100,
  className,
  label = "Drop files here or click to browse",
  onUploaded,
  "data-testid": testId = "file-dropzone",
}) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState([]); // {name, pct}

  const uploadOne = useCallback(async (file) => {
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`${file.name} is over ${maxMB}MB — please compress or split.`);
      return;
    }
    const rowKey = `${file.name}-${Date.now()}-${Math.random()}`;
    setBusy((b) => [...b, { key: rowKey, name: file.name, pct: 0, size: file.size }]);
    try {
      const record = await uploadFile(file, context, (pct) => {
        setBusy((b) => b.map((x) => (x.key === rowKey ? { ...x, pct } : x)));
      });
      onUploaded?.(record);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload failed: ${file.name}`);
    } finally {
      setBusy((b) => b.filter((x) => x.key !== rowKey));
    }
  }, [context, maxMB, onUploaded]);

  const handleFiles = useCallback((files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    (multiple ? list : list.slice(0, 1)).forEach(uploadOne);
  }, [multiple, uploadOne]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={cn("space-y-2", className)}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={cn(
          "w-full rounded-xl border-2 border-dashed text-left transition-colors",
          drag
            ? "border-[hsl(var(--studio-primary))] bg-[hsl(var(--studio-primary)/0.05)]"
            : "border-border hover:border-foreground/25 bg-muted/30",
          compact ? "px-3 py-2 flex items-center gap-2" : "p-6 flex flex-col items-center justify-center gap-2 text-center"
        )}
        data-testid={`${testId}-target`}
      >
        <UploadCloud
          className={cn(
            "text-muted-foreground",
            compact ? "w-4 h-4" : "w-6 h-6",
            drag && "text-[hsl(var(--studio-primary))]"
          )}
        />
        <div className={compact ? "text-xs" : "text-sm"}>
          <span className={cn("font-medium", drag && "text-[hsl(var(--studio-primary))]")}>{label}</span>
          {!compact && (
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Up to {maxMB} MB · {multiple ? "multiple files supported" : "single file"}
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid={`${testId}-input`}
        />
      </button>

      {busy.length > 0 && (
        <div className="space-y-1" data-testid={`${testId}-progress`}>
          {busy.map((row) => (
            <div key={row.key} className="text-xs flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5">
              <div className="truncate flex-1">
                <span className="font-medium">{row.name}</span>
                <span className="text-muted-foreground ml-1">· {humanBytes(row.size)}</span>
              </div>
              <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-[hsl(var(--studio-primary))] transition-[width]"
                  style={{ width: `${row.pct}%` }}
                />
              </div>
              <span className="tabular-nums w-8 text-right text-muted-foreground">{row.pct}%</span>
              <X className="w-3 h-3 text-muted-foreground/50" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
